import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageHead
        title="Datenschutz | NetBasis International"
        description="Informationen zum Schutz Ihrer personenbezogenen Daten und zur Datenerfassung bei NetBasis International gemäß DSGVO."
        ogUrl="https://net-basis.net/de/datenschutz"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero Image Section — identisch zu Home/About Hero Struktur */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src="https://net-basis.net/Bilder/netbasis-legal-notice-privacy-policy.webp"
                alt="Datenschutzerklärung"
                title="Datenschutzerklärung"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
            </div>
          </div>
        </section>

        {/* Content Section — Abstand zum Bild identisch zu Home (IntroSection: py-6 md:py-8) */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            {/* H1 — Art, Form & Größe wie Home "NetBasis International - linking people & business" */}
            <h1 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              Datenschutzerklärung
            </h1>

            {/* ========== SECTION 1 ========== */}
            <section className="mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
                1. Datenschutz auf einen Blick
              </h2>

              <div className="mb-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Allgemeine Hinweise
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie unsere Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600">
                  Datenerfassung auf unserer Website
                </h3>

                <div>
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                    <strong className="font-semibold text-foreground-950">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong><br />
                    Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Abschnitt „Hinweis zur verantwortlichen Stelle" in dieser Datenschutzerklärung entnehmen.
                  </p>
                </div>

                <div>
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                    <strong className="font-semibold text-foreground-950">Wie erfassen wir Ihre Daten?</strong><br />
                    Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z. B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
                  </p>
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                    Andere Daten werden automatisch beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z. B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs). Die Erfassung dieser Daten erfolgt automatisch, sobald Sie unsere Website betreten.
                  </p>
                </div>

                <div>
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                    <strong className="font-semibold text-foreground-950">Wofür nutzen wir Ihre Daten?</strong><br />
                    Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
                  </p>
                </div>

                <div>
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                    <strong className="font-semibold text-foreground-950">Welche Rechte haben Sie bezüglich Ihrer Daten?</strong><br />
                    Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen. Unter bestimmten Umständen haben Sie zudem das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen sowie ein Widerspruchsrecht gegen die Verarbeitung. Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit unter der im Impressum angegebenen Adresse an uns wenden. Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.
                  </p>
                </div>
              </div>
            </section>

            {/* Divider */}
            <div className="w-full h-px bg-background-300 my-10" />

            {/* ========== SECTION 2 ========== */}
            <section className="mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
                2. Hosting
              </h2>

              <div className="mb-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Externes Hosting durch STRATO
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Unsere Website wird bei einem externen Dienstleister gehostet (Hoster). Die personenbezogenen Daten, die auf dieser Website erfasst werden, werden auf den Servern des Hosters gespeichert. Hierbei handelt es sich v. a. um IP-Adressen, Kontaktanfragen, Meta- und Kommunikationsdaten, Webseitenzugriffe und sonstige Daten, die über eine Website generiert werden.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  Der Einsatz des Hosters erfolgt zum Zwecke der Vertragserfüllung gegenüber unseren potenziellen und bestehenden Kunden (Art. 6 Abs. 1 lit. b DSGVO) und im Interesse einer sicheren, schnellen und effizienten Bereitstellung unseres Online-Angebots durch einen professionellen Anbieter (Art. 6 Abs. 1 lit. f DSGVO).
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  Wir setzen folgenden Hoster ein:<br />
                  STRATO AG<br />
                  Otto-Ostrowski-Straße 7<br />
                  10249 Berlin
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  <strong className="font-semibold text-foreground-950">Abschluss eines Vertrages über Auftragsverarbeitung:</strong><br />
                  Um die datenschutzkonforme Verarbeitung zu gewährleisten, ist ein Vertrag über Auftragsverarbeitung (AVV) mit der STRATO AG aktiv (integriert in die Allgemeinen Geschäftsbedingungen von STRATO). Dieser garantiert, dass der Hoster die personenbezogenen Daten unserer Websitebesucher nur nach unseren Weisungen und unter Einhaltung der DSGVO verarbeitet.
                </p>
              </div>

              <div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Server-Logfiles
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Der Provider der Seiten erhebt und speichert automatisch Informationen in sogenannten Server-Logfiles, die Ihr Browser automatisch an uns übermittelt. Dies sind:
                </p>
                <ul className="mt-3 space-y-1.5 list-disc pl-6 font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  <li>Browsertyp und Browserversion</li>
                  <li>Verwendetes Betriebssystem</li>
                  <li>Referrer URL (die zuvor besuchte Seite)</li>
                  <li>Hostname des zugreifenden Rechners</li>
                  <li>Uhrzeit der Serveranfrage</li>
                  <li>IP-Adresse</li>
                </ul>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen. Die Erfassung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes Interesse an der technisch fehlerfreien Darstellung und der Optimierung seiner Website – hierzu müssen die Server-Logfiles erfasst werden. Die Daten werden nach <strong className="font-semibold text-foreground-950">7 Tagen</strong> automatisch gelöscht.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Cloudflare CDN (Font Awesome & Remix Icon)
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Wir nutzen für die Bereitstellung von Symbolen und Icons (Font Awesome und Remix Icon) das Content Delivery Network (CDN) von Cloudflare. Anbieter ist die Cloudflare Inc., 101 Townsend St, San Francisco, CA 94107, USA.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  Ein CDN ist ein Dienst, mit dessen Hilfe Inhalte unserer Website, insbesondere Mediendateien oder Skripte, mithilfe eines Netzes regionaler oder international verteilter Server schneller ausgeliefert werden. Bei dem Aufruf dieser Symbole stellt Ihr Browser eine direkte Verbindung zu den Servern von Cloudflare her. Hierbei wird Ihre IP-Adresse und die Information, welche unserer Seiten Sie besucht haben, an Cloudflare übertragen.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  Die Nutzung des CDNs erfolgt auf Grundlage unseres berechtigten Interesses an einer sicheren Bereitstellung unserer Website (Art. 6 Abs. 1 lit. f DSGVO). Die Datenübertragung in die USA wird auf die Standardvertragsklauseln der EU-Kommission gestützt. Details finden Sie in der Datenschutzerklärung von Cloudflare: https://cloudflare.com.
                </p>
              </div>
            </section>

            {/* Divider */}
            <div className="w-full h-px bg-background-300 my-10" />

            {/* ========== SECTION 3 ========== */}
            <section>
              <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
                3. Allgemeine Hinweise und Pflichtinformationen
              </h2>

              <div className="mb-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Datenschutz
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  Beim Aufruf unserer Website werden <strong className="font-semibold text-foreground-950">keine Analyse- oder Tracking-Cookies</strong> eingesetzt. Es wird lediglich ein technisch notwendiges Cookie gesetzt, um Ihre Entscheidung bezüglich der Aktivierung des Kartendienstes Google Maps zu speichern, sofern Sie diesen nutzen.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  Wir weisen darauf hin, dass die Datenübertragung im Internet (z. B. bei der Kommunikation per E-Mail) Sicherheitslücken aufweisen kann. Im lückenloser Schutz der Daten vor dem Zugriff durch Dritte ist nicht möglich.
                </p>
              </div>

              <div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Hinweis zur verantwortlichen Stelle
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
                </p>
                <div className="mt-3 p-5 bg-background-50 border border-background-200 rounded-lg">
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                    Christof Günther<br />
                    NetBasis International<br />
                    Heinrichstraße 45<br />
                    40239 Düsseldorf
                  </p>
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                    Telefon: +49 (0) 211 / 544 193 80<br />
                    E-Mail: kontakt@net-basis.com
                  </p>
                </div>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-4">
                  Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten (z. B. Namen, E-Mail-Adressen o. Ä.) entscheidet.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Speicherdauer
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Sofern innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer genannt wurde, verbleiben Ihre personenbezogenen Daten bei uns, bis der Zweck für die Datenverarbeitung entfällt. Wenn Sie ein berechtigtes Löschbegehren geltend machen oder eine Einwilligung zur Datenverarbeitung widerrufen, werden Ihre Daten gelöscht, sofern wir keine anderen rechtlich zulässigen Gründe für die Speicherung Ihrer personenbezogenen Daten haben (z. B. steuer- oder handelsrechtliche Aufbewahrungsfristen); im letztgenannten Fall erfolgt die Löschung nach Fortfall dieser Gründe.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Widerruf Ihrer Einwilligung zur Datenverarbeitung
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie können eine bereits erteilte Einwilligung jederzeit widerrufen. Dazu reicht eine formlose Mitteilung per E-Mail an uns. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf unberührt.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Widerspruchsrecht gegen die Datenerhebung in besonderen Fällen sowie gegen Direktwerbung (Art. 21 DSGVO)
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  WENN DIE DATENVERARBEITUNG AUF GRUNDLAGE VON ART. 6 ABS. 1 LIT. E ODER F DSGVO ERFOLGT, HABEN SIE JEDERZEIT DAS RECHT, AUS GRÜNDEN, DIE SICH AUS IHRER BESONDEREN SITUATION ERGEBEN, GEGEN DIE VERARBEITUNG IHER PERSONENBEZOGENEN DATEN WIDERSPRUCH EINZULEGEN; DIES GILT AUCH FÜR EIN AUF DIESE BESTIMMUNGEN GESTÜTZTES PROFILING. DIE JEWEILIGE RECHTSGRUNDLAGE, AUF DENEN EINE VERARBEITUNG BERUHT, ENTNEHMEN SIE DIESER DATENSCHUTZERKLÄRUNG. WENN SIE WIDERSPRUCH EINLEGEN, WERDEN WIR IHRE BETROFFENEN PERSONENBEZOGENEN DATEN NICHT MEHR VERARBEITEN, ES SEI DENN, WIR KÖNNEN ZWINGENDE SCHUTZWÜRDIGE GRÜNDE FÜR DIE VERARBEITUNG NACHWEISEN, DIE IHRE INTERESSEN, RECHTE UND FREIHEITEN ÜBERWIEGEN ODER DIE VERARBEITUNG DIENT DER GELTENDMACHUNG, AUSÜBUNG ODER VERTEIDIGUNG VON RECHTSANSPRÜCHEN (WIDERSPRUCH NACH ART. 21 ABS. 1 DSGVO).
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  WERDEN IHRE PERSONENBEZOGENEN DATEN VERARBEITET, UM DIREKTWERBUNG ZU BETREIBEN, SO HABEN SIE DAS RECHT, JEDERZEIT WIDERSPRUCH GEGEN DIE VERARBEITUNG SIE BETREFFENDER PERSONENBEZOGENER DATEN ZUM ZWECKE DERARTIGER WERBUNG EINZULEGEN; DIES GILT AUCH FÜR DAS PROFILING, SOWEIT ES MIT SOLCHER DIREKTWERBUNG IN VERBINDUNG STEHT. WENN SIE WIDERSPRECHEN, WERDEN IHRE PERSONENBEZOGENEN DATEN ANSCHLIESSEND NICHT MEHR ZUM ZWECKE DER DIREKTWERBUNG VERWENDET (WIDERSPRUCH NACH ART. 21 ABS. 2 DSGVO).
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Beschwerderecht bei der zuständigen Aufsichtsbehörde
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht bei einer Aufsichtsbehörde, insbesondere in dem Mitgliedstaat ihres gewöhnlichen Aufenthalts, ihres Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes zu. Das Beschwerderecht besteht unbeschadet anderweitiger verwaltungsrechtlicher oder gerichtlicher Rechtsbehelfe.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Recht auf Datenübertragbarkeit
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in Erfüllung eines Vertrags automatisiert verarbeiten, an sich oder an einen Dritten in einem gängigen, maschinenlesbaren Format aushändigen zu lassen. Sofern Sie die direkte Übertragung der Daten an einen anderen Verantwortlichen verlangen, erfolgt dies nur, soweit es technisch machbar ist.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  SSL- bzw. TLS-Verschlüsselung
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, wie zum Beispiel Bestellungen oder Anfragen, die Sie an uns als Seitenbetreiber senden, eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt und an dem Schloss-Symbol in Ihrer Browserzeile. Wenn die SSL- bzw. TLS-Verschlüsselung aktiviert ist, können die Daten, die Sie an uns übermitteln, nicht von Dritten mitgelesen werden.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Auskunft, Berichtigung, Einschränkung der Verarbeitung und Löschung
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten, deren Herkunft und Empfänger und den Zweck der Datenverarbeitung und ggf. ein Recht auf Berichtigung oder Löschung dieser Daten.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  Sie haben zudem das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Dies ist in folgenden Fällen möglich:
                </p>
                <ul className="mt-3 space-y-1.5 list-disc pl-6 font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  <li>Wenn Sie die Richtigkeit Ihrer Daten bestreiten (für die Dauer der Prüfung).</li>
                  <li>Bei unrechtmäßiger Verarbeitung statt Löschung.</li>
                  <li>Bei Wegfall des Verarbeitungszwecks, aber Notwendigkeit zur Rechtsverteidigung.</li>
                  <li>Nach einem Widerspruch gem. Art. 21 Abs. 1 DSGVO während der Interessenabwägung.</li>
                </ul>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  Hierzu sowie zu weiteren Fragen zum Thema personenbezogene Daten können Sie sich jederzeit an uns wenden.
                </p>
              </div>
            </section>

            {/* Divider */}
            <div className="w-full h-px bg-background-300 my-10" />

            {/* ========== SECTION 4 ========== */}
            <section className="mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
                4. Plugins und Tools
              </h2>

              <div className="mb-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Google Fonts (Lokale Einbindung)
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Wir nutzen auf dieser Website Google Fonts sowie Symbol-Bibliotheken (Font Awesome und Remix Icon) zur einheitlichen und gestalterisch ansprechenden Darstellung von Texten und Icons.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  <strong className="font-semibold text-foreground-950">Das Besondere:</strong> Wir haben sämtliche Schriftarten und Icons <strong className="font-semibold text-foreground-950">lokal auf unserem eigenen Webserver bei STRATO installiert</strong>. Beim Aufruf unserer Website findet somit <strong className="font-semibold text-foreground-950">keine Verbindung zu externen Servern</strong> von Google, Cloudflare oder sonstigen Drittanbietern statt. Es werden keinerlei IP-Adressen oder Verbindungsdaten weitergegeben. Die Nutzung erfolgt auf Basis unseres berechtigten Interesses an einer sicheren, schnellen und optisch einheitlichen Webseitenbereitstellung (Art. 6 Abs. 1 lit. f DSGVO).
                </p>
              </div>

              <div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Google Maps (Mit Zwei-Klick-Lösung)
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Wir nutzen auf unserer Kontaktseite den Kartendienst Google Maps, um Ihnen die Anfahrt zu uns zu erleichtern. Anbieter ist die Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  <strong className="font-semibold text-foreground-950">Schutz Ihrer Privatsphäre (Zwei-Klick-Lösung):</strong><br />
                  Damit Ihre Daten beim bloßen Aufrufen der Kontaktseite geschützt bleiben, ist Google Maps standardmäßig deaktiviert. Es wird zunächst nur ein neutrales Vorschaubild angezeigt. Erst wenn Sie aktiv auf die Karte klicken („Inhalte aktivieren"), erteilen Sie uns Ihre Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). Erst ab diesem Moment wird die Karte geladen und Ihre IP-Adresse an Server von Google übertragen. Zur Speicherung Ihrer Entscheidung wird ein technisch notwendiges Cookie im Browser gesetzt. Details zum Umgang mit Nutzerdaten finden Sie direkt in der Datenschutzerklärung von Google: https://google.com.
                </p>
              </div>
            </section>

            {/* Divider */}
            <div className="w-full h-px bg-background-300 my-10" />

            {/* ========== SECTION 5 ========== */}
            <section>
              <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
                5. Formulare und Kontaktaufnahme
              </h2>

              <div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Kontaktformular
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung auf unserem berechtigten Interesse an der effektiven Bearbeitung der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO).
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie uns zur Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die Datenspeicherung entfällt (z. B. nach abgeschlossener Bearbeitung Ihrer Anfrage). Zwingende gesetzliche Bestimmungen – insbesondere gesetzliche Aufbewahrungsfristen (z. B. bei steuerlich relevanten Geschäftsmails) – bleiben unberührt.
                </p>
              </div>
            </section>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}